import React, { useState, useRef } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { LinkIcon, ChevronDownIcon, LinkedinIcon, YoutubeIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { advisors, Advisor } from '../data/advisors';
function AdvisorCard({
  advisor,
  index
}: {
  advisor: Advisor;
  index: number;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, {
    once: true,
    margin: '-50px'
  });
  const [bioOpen, setBioOpen] = useState(false);
  const {
    language
  } = useLanguage();
  const isEs = language === 'ES';
  const esTitles: Record<string, string> = {
    DB: 'Miembro de la Junta Escolar',
    EC: 'Presidenta y CEO Emérita',
    SF: 'Fundador',
    TK: 'Senador de EE.UU.',
    MF: 'Secretario de Salud y Recursos Humanos',
    AR: 'CEO — Jubilado',
    AF: 'Actriz y Directora',
    JQ: 'Sheriff',
    BG: 'Chef y Propietario',
    MH: 'Propietaria Mayoritaria y Presidenta',
    MY: 'Entrenadora Principal de Softbol'
  };
  const esBios: Record<string, string> = {
    DB: 'Diana Brown forma parte de la Junta Escolar de la Ciudad de Manassas, donde es una defensora visible de escuelas públicas sólidas, entornos de aprendizaje inclusivos y la toma de decisiones centrada en los estudiantes. Fue elegida en 2025 para un mandato de cuatro años, y también es Vicepresidenta de la Junta del Northern Virginia Community College. Inmigrante de Honduras y educadora galardonada, Brown aporta una profunda experiencia en liderazgo de educación especial y ha sido reconocida a nivel estatal por su trabajo apoyando a estudiantes y maestros.',
    EC: 'Eva Callejas es una líder cívica hondureña que anteriormente se desempeñó como Directora Ejecutiva (y figura principal) de Honduras Global, una red de conocimiento de la diáspora enfocada en conectar el talento hondureño en el exterior con iniciativas de desarrollo en el país. En ese rol, ayudó a construir alianzas con actores gubernamentales y educativos para avanzar la innovación, la tecnología y las oportunidades para las comunidades hondureñas.',
    SF: 'Shin Fujiyama es un filántropo japonés-americano y cofundador/director ejecutivo de Students Helping Honduras, una organización sin fines de lucro enfocada en ampliar el acceso a la educación. Dedicó su vida a construir escuelas y apoyar comunidades en todo el país a través de la visión de Mil Escuelas.',
    TK: 'Tim Kaine sirve como Senador de EE.UU. por Virginia, aportando décadas de servicio público como ex Gobernador y Vicegobernador del Estado. Sus lazos con Honduras son profundamente personales: al inicio de su carrera vivió en El Progreso, Honduras, trabajando con misioneros jesuitas en una escuela vocacional.',
    MF: 'Marvin B. Figueroa sirve como Secretario de Salud y Recursos Humanos de Virginia, liderando el trabajo del Estado para fortalecer la salud pública, ampliar el acceso a la atención médica y mejorar la prestación de servicios humanos. Originario de La Ceiba, Honduras.',
    AR: 'Alfredo Rivera es un ejecutivo empresarial nacido en Honduras y líder de larga trayectoria en Coca-Cola que ascendió a los rangos regionales más altos de la compañía después de más de tres décadas en el sistema de Coca-Cola.',
    AF: 'America Ferrera es una actriz, productora y activista galardonada cuyas raíces hondureñas han moldeado durante mucho tiempo su voz pública y su defensa. Nacida en Los Ángeles de padres de Tegucigalpa, Honduras.',
    JQ: 'Jose Quiroz sirve como Sheriff del Condado de Arlington, Virginia, liderando la Oficina del Sheriff en sus responsabilidades de operaciones carcelarias, seguridad del tribunal y servicios de seguridad pública orientados a la comunidad.',
    BG: 'Bryan Girón es un chef nacido en Honduras conocido por mezclar los sabores intensos de Centroamérica con influencias mediterráneas y globales. Nacido en Tegucigalpa, Honduras, y criado en Nueva Jersey, Girón descubrió su pasión por la cocina a través de su abuela, cuyas recetas y tradiciones de mercado siguen inspirando su cocina hoy. Después de estudiar en Johnson & Wales University, construyó un impresionante currículum culinario trabajando en cocinas reconocidas como Shukette, Barbuto, Bread + Salt, Beauty & Essex y The Stanton Social. Es chef y propietario de Perquín en Jersey City, donde muestra la cocina hondureña moderna a través de platos cuidadosamente elaborados arraigados en la herencia, los viajes y la cocina con fuego vivo. En 2026, Girón obtuvo reconocimiento nacional después de convertirse en el primer chef nacido en Honduras en ganar Chopped de Food Network.',
    MH: 'Martha Hernandez es una fuerza dinámica tanto en el mundo de los negocios como en el filantrópico. Como Propietaria Mayoritaria y Presidenta de Extreme Wood Works of South Florida, Inc., ha construido una carrera definida por la pasión, la perseverancia y el propósito. Originaria de Honduras, asumió el liderazgo del negocio familiar a tan solo 20 años mientras equilibraba su vida como joven madre y estudiante universitaria. En 2020, tras la devastación de los huracanes Eta e Iota, lideró el envío de cinco contenedores de ayuda humanitaria a Honduras y Nicaragua, un esfuerzo que dio origen a Amor y Fuerza, una organización sin fines de lucro que continúa elevando a comunidades vulnerables en Miami y a nivel internacional. Dedicada al servicio y al impacto social, Martha lidera con innovación, compasión y una energía inspiradora que motiva a quienes la rodean.',
    MY: 'Marissa Young es una orgullosa líder hondureño-americana y una de las figuras más destacadas del softbol universitario de la actualidad. Como entrenadora principal inaugural del programa de softbol de Duke University, construyó un contendiente nacional desde cero, guiando a las Blue Devils a múltiples apariciones en el Torneo de la NCAA, campeonatos de la ACC y el primer viaje del programa a la Women\u2019s College World Series en 2024, una temporada histórica con un récord del programa de 52 victorias que le valió el honor de Entrenadora del Año de la ACC. Lanzadora All-American en tres ocasiones en la University of Michigan, se graduó como la líder histórica de ponches del programa con 927 ponches y luego jugó profesionalmente en Italia y los Estados Unidos. Un modelo a seguir pionero, su liderazgo, perseverancia y compromiso con la excelencia continúan inspirando a atletas y familias en todo el país.'
  };
  const displayTitle = isEs ? esTitles[advisor.initials] || advisor.title : advisor.title;
  const displayBio = isEs ? esBios[advisor.initials] || advisor.bio : advisor.bio;
  return <motion.article ref={ref} initial={{
    opacity: 0,
    y: 50,
    rotate: index % 2 === 0 ? -2 : 2
  }} animate={inView ? {
    opacity: 1,
    y: 0,
    rotate: 0
  } : {}} transition={{
    duration: 0.7,
    delay: index * 0.1,
    ease: [0.16, 1, 0.3, 1]
  }} className={`bg-white rounded-2xl shadow-lg overflow-hidden border-l-4 ${advisor.borderColor} group card-hover flex flex-col`} data-id="element-213" data-mp="ctyr8jqmf4gw">
      <div className="p-6 flex flex-col flex-1" data-id="element-214" data-mp="rhfnhzq7hlf5">
        {/* Avatar — unified layout for all cards */}
        <div className="flex items-start gap-4 mb-4" data-id="element-215" data-mp="wg0hhhev78v5">
          {advisor.photo ? <motion.div whileHover={{
          scale: 1.05
        }} className={`w-40 h-40 rounded-2xl bg-gradient-to-br ${advisor.gradient} flex-shrink-0 shadow-lg overflow-hidden`} data-id="element-216" data-mp="iovmwtqct6ro">
              <img src={advisor.photo} alt={advisor.name} className="w-full h-full object-cover object-[center_30%]" style={undefined} data-id="element-217" data-mp="8rr0csslj9ac" />
            </motion.div> : <motion.div whileHover={{
          scale: 1.05,
          rotate: 5
        }} className={`w-40 h-40 rounded-2xl bg-gradient-to-br ${advisor.gradient} flex items-center justify-center flex-shrink-0 shadow-lg overflow-hidden`} data-id="element-218" data-mp="3rlbtlhmi2wh">
              <span className="font-bebas text-white text-4xl tracking-wider" data-id="element-219" data-mp="a8xxhpsuge0p">
                {advisor.initials}
              </span>
            </motion.div>}
          <div className="flex-1 min-w-0" data-id="element-220" data-mp="59lejg53fr3e">
            <h3 className="font-bebas text-xl text-haa-navy tracking-wider leading-tight" data-id="element-221" data-mp="18rpgjfx068x">
              {advisor.name}
            </h3>
            <p className={`font-nunito font-bold text-sm ${advisor.accentColor} uppercase tracking-wider mt-1`} data-id="element-222" data-mp="3slckyvir9ao8">
              {displayTitle}
            </p>
            <p className="font-nunito text-xs text-haa-skyblue font-semibold tracking-wide mt-0.5" data-id="element-223" data-mp="3ipsq8cfxgi12">
              {advisor.subtitle}
            </p>
          </div>
        </div>

        {/* Bio Dropdown Toggle */}
        <button onClick={() => setBioOpen(!bioOpen)} aria-expanded={bioOpen} className={`w-full flex items-center justify-between gap-2 py-2.5 px-3 rounded-xl mb-1 text-left transition-colors duration-200 ${bioOpen ? 'bg-gray-50' : 'hover:bg-gray-50'}`} data-id="element-224" data-mp="39ol5yya8xhpz">
          <span className={`font-nunito font-semibold text-sm ${advisor.accentColor}`} data-id="element-225" data-mp="1y80co72ziijy">
            {bioOpen ? isEs ? 'Ocultar Biografía' : 'Hide Bio' : isEs ? 'Leer Biografía' : 'Read Bio'}
          </span>
          <motion.div animate={{
          rotate: bioOpen ? 180 : 0
        }} transition={{
          duration: 0.25,
          ease: [0.16, 1, 0.3, 1]
        }} className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center bg-gradient-to-br ${advisor.gradient}`} data-id="element-226" data-mp="1af1psbiir414">
            <ChevronDownIcon className="w-3.5 h-3.5 text-white" data-id="element-227" data-mp="rpv6ondtcaes" />
          </motion.div>
        </button>

        {/* Animated Bio */}
        <AnimatePresence initial={false} data-id="element-228" data-mp="3spnq1z2jcgta">
          {bioOpen && <motion.div key="bio" initial={{
          height: 0,
          opacity: 0
        }} animate={{
          height: 'auto',
          opacity: 1
        }} exit={{
          height: 0,
          opacity: 0
        }} transition={{
          duration: 0.35,
          ease: [0.16, 1, 0.3, 1]
        }} className="overflow-hidden" data-id="element-229" data-mp="1ikyv7mp6aj2">
              <p className="font-nunito text-gray-600 text-sm leading-relaxed pt-2 pb-4 px-1" data-id="element-230" data-mp="3jw0x0gppprns">
                {displayBio}
              </p>
            </motion.div>}
        </AnimatePresence>

        {/* Link button */}
        <div className="flex items-center gap-2 pt-4 mt-auto border-t border-gray-100" data-id="element-231" data-mp="2r73qfauv5jav">
          {advisor.linkedin && <motion.a href={advisor.linkedin} target="_blank" rel="noopener noreferrer" aria-label={`${advisor.name} LinkedIn`} whileHover={{
          scale: 1.15
        }} whileTap={{
          scale: 0.9
        }} className={`w-8 h-8 rounded-lg bg-gradient-to-br ${advisor.gradient} flex items-center justify-center`} data-id="element-232" data-mp="1dcmxdw0uzqa1">
              <LinkedinIcon className="w-4 h-4 text-white" data-id="element-233" data-mp="ug59xqt1mcjl" />
            </motion.a>}
          {advisor.link && <motion.a href={advisor.link} target="_blank" rel="noopener noreferrer" aria-label={`${advisor.name} profile link`} whileHover={{
          scale: 1.15
        }} whileTap={{
          scale: 0.9
        }} className={`w-8 h-8 rounded-lg bg-gradient-to-br ${advisor.gradient} flex items-center justify-center`} data-id="element-234" data-mp="llqmv229txpx">
              <LinkIcon className="w-4 h-4 text-white" data-id="element-235" data-mp="3t4nhy8ceoqiz" />
            </motion.a>}
          {advisor.youtube && <motion.a href={advisor.youtube} target="_blank" rel="noopener noreferrer" aria-label={`${advisor.name} video`} whileHover={{
          scale: 1.15
        }} whileTap={{
          scale: 0.9
        }} className={`w-8 h-8 rounded-lg bg-gradient-to-br ${advisor.gradient} flex items-center justify-center`} data-id="element-236" data-mp="1xkotbd0ca64">
              <YoutubeIcon className="w-4 h-4 text-white" data-id="element-237" data-mp="6y8nmj7h9vaw" />
            </motion.a>}
        </div>
      </div>
    </motion.article>;
}
export function AdvisoryCouncil() {
  const titleRef = useRef<HTMLDivElement>(null);
  const titleInView = useInView(titleRef, {
    once: true
  });
  const {
    language
  } = useLanguage();
  const isEs = language === 'ES';
  return <section id="advisory-council" className="bg-white py-20 lg:py-28" aria-labelledby="advisory-heading" data-id="element-238" data-mp="2o6th9m7ctmts">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-239" data-mp="2j7gut2keahrq">
        {/* Header */}
        <div ref={titleRef} className="text-center mb-14" data-id="element-240" data-mp="2xw3chaxhvlm4">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={titleInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6
        }} data-id="element-241" data-mp="2sy1wwzza24fe">
            <span className="font-nunito text-haa-blue text-sm uppercase tracking-[0.3em] font-semibold" data-id="element-242" data-mp="2f4ka96baobl5">
              {isEs ? 'Miembros del Consejo Asesor' : 'Advisory Members'}
            </span>
            <h2 id="advisory-heading" className="font-bebas text-6xl sm:text-7xl lg:text-8xl text-haa-navy tracking-wider mt-2" data-id="element-243" data-mp="3qnwsp2dk073b">
              {isEs ? 'Consejo Asesor Nacional' : 'National Advisory Council'}
            </h2>
            <div className="flex items-center justify-center gap-3 mt-4" data-id="element-244" data-mp="205q4dxymyvqe">
              <div className="w-12 h-1 bg-haa-blue rounded-full" data-id="element-245" data-mp="245sacjbembbh" />
              <div className="w-3 h-3 rounded-full bg-haa-skyblue" data-id="element-246" data-mp="248mt86erro2s" />
              <div className="w-12 h-1 bg-haa-midblue rounded-full" data-id="element-247" data-mp="296y32h52dyl0" />
            </div>
            <p className="font-nunito text-gray-600 text-lg mt-6 max-w-2xl mx-auto" data-id="element-248" data-mp="2e3eo32gyvwtv">
              {isEs ? 'Líderes distinguidos a quienes honramos por sus extraordinarias contribuciones a la comunidad hondureño-americana.' : 'Distinguished leaders we honor for their extraordinary contributions to the Honduran-American community.'}
            </p>
          </motion.div>
        </div>

        {/* Advisors Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-249" data-mp="2wt4n61rrj4rl">
          {advisors.map((advisor, i) => <AdvisorCard key={`${advisor.name}-${i}`} advisor={advisor} index={i} data-id="element-250" data-mp="3byifqs7hgnpi" data-mpi={i} />)}
        </div>
      </div>
    </section>;
}